import { EReportTaskStatus, type IReportCardInfo, type REPORT_TEMPLATE } from '@/types';
import { AbstractModelImpl } from '../common/abstract-model';
import type { ReportApiService } from '@/infrastructure/api-functions/report-api-service';

export interface BaseReportCardData<T> {
    isLoading: boolean;
    isFailed: boolean;
    isSuccess: boolean;
    data?: T;
}

export interface BaseCardService<F> extends AbstractModelImpl<BaseReportCardData<F>> {
    startFetch(projectId: string, groupKey: string, isRetry?: boolean): Promise<void> | undefined;
    updateFetchData(getUpdateValue: (v: BaseReportCardData<IReportCardInfo>) => BaseReportCardData<IReportCardInfo>): void;
    templateType: REPORT_TEMPLATE;
}

export abstract class BaseCardServiceImpl<T> extends AbstractModelImpl<BaseReportCardData<T>> implements BaseCardService<T> {
    abstract updateFetchData(getUpdateValue: (v: BaseReportCardData<IReportCardInfo>) => BaseReportCardData<IReportCardInfo>): void

    abstract templateType: REPORT_TEMPLATE;

    private _timer: number | null = null;

    private _isFetching = false

    protected _reportApiService: ReportApiService;

    constructor(reportApiService: ReportApiService) {
        super();
        this._reportApiService = reportApiService;
    }

    startFetch(projectId: string, groupKey: string, isRetry?: boolean) {
        if (this._isFetching) {
            return;
        }
        if (isRetry) {
            // 重试清空状态
            this.updateFetchData((v) => { return { ...v, isFailed: false, data: undefined } });
        }

        this._isFetching = true;

        this.updateFetchData((v) => { return { ...v, isLoading: true } });

        return this._reportApiService.getReportCardData({ projectId, taskGroupKey: groupKey, isRetry })
            .then(detail => {
                this._isFetching = false;
                if (detail) {
                    if (detail.task_status === EReportTaskStatus.PROCESSING) {
                        this._timer && clearTimeout(this._timer);
                        this._isFetching = true;
                        setTimeout(() => this.startFetch(projectId, groupKey), 2000);
                    } else {
                        const isFailed = detail.task_status === EReportTaskStatus.FAILED;
                        const isSuccess = detail.task_status === EReportTaskStatus.FINISH;
                        this.updateFetchData((v) => { return { ...v, data: detail, isFailed, isSuccess, isLoading: false } });
                    }
                }
            })
            .catch(e => {
                this.updateFetchData((v) => { return { ...v, isLoading: false, isFailed: true } });
                throw e;
            })
            .finally(() => {
                this._isFetching = false;
            });
    }
}
